import { ReactNode, useState } from 'react';

import { useSelector } from 'react-redux';

import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
import { cn } from '@/lib/utils';
import { selectIsReplay } from '@/modules/tryIt/redux/selectors';
import { isMessageFromCollection, removeDuplicateDocuments } from '@/modules/tryIt/utils/helper';
import { AmendPayload, Correction, ResponseMessage, TryItCollection } from '@/shared/types/tryIt';

import { bubbleBrainClasses, responseClasses } from './constants';
import { FragmentBadges } from './FragmentBadges';
import { RetrievedSources } from './RetrievedSources';
import { AmendWrapper } from '../../AmendWrapper/AmendWrapper';

const optionClasses = 'py-2 px-3 bg-muted rounded-lg text-sm transition-all duration-150';

type TextMessage = Extract<ResponseMessage, { type: 'text' }>;

interface TextResponseProps {
  response: TextMessage;
  turnRow?: boolean;
  collection?: TryItCollection;
  onOptionClick: (text: string) => void;
  amendPayload?: AmendPayload;
  correction?: Correction;
  icon: ReactNode;
  goToButton: ReactNode;
  flat?: boolean;
}

export const TextResponse = ({
  response,
  collection,
  onOptionClick,
  amendPayload,
  correction,
  icon,
  goToButton,
  flat = false,
  turnRow = false,
}: TextResponseProps) => {
  const isCollectionReply = isMessageFromCollection(collection);

  const text = 'text' in response ? response.text : '';
  const { html } = useMarkdownToHtml(text);
  const uniqueDocuments = removeDuplicateDocuments(collection?.fragments || []);

  const external_urls = collection?.external_urls?.filter((url) => !!url.document_id);
  const fragmentCount = external_urls?.length || uniqueDocuments?.length || 0;
  const hasSelectedDocuments = (external_urls?.length ?? 0) > 0;

  const [optionChosen, setOptionChosen] = useState(false);
  const isReplay = useSelector(selectIsReplay);

  const handleOptionClick = (text: string) => {
    if (!isReplay) {
      onOptionClick(text);
      setOptionChosen(true);
    }
  };

  // for test webhook
  if ('texts' in response && response.texts) {
    const texts = response.texts as string[];
    return (
      <>
        {texts.map((text, index) => (
          <p
            // eslint-disable-next-line react/no-array-index-key
            key={`${text}_${index}`}
            className={bubbleBrainClasses}
          >
            {text}
          </p>
        ))}
        {!optionChosen && response.options && (
          <div className="my-2 mr-9 flex flex-wrap justify-end gap-2 text-right">
            {response.options.map((option) => (
              <button
                key={`option-${option.text}`}
                className={cn(optionClasses, { 'pointer-events-none': isReplay })}
                onClick={() => handleOptionClick(option.text)}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </>
    );
  }

  const hasSources = isCollectionReply && fragmentCount > 0;
  const footer = (() => {
    if (!hasSources) {
      return null;
    }
    return turnRow ? (
      <RetrievedSources count={fragmentCount} />
    ) : (
      <FragmentBadges count={fragmentCount} isSelected={hasSelectedDocuments} />
    );
  })();

  const body = (
    <AmendWrapper
      flat={flat}
      amendPayload={amendPayload}
      isAmended={!!correction}
      correctionText={correction?.correction}
      originalText={turnRow ? text : undefined}
      turnRow={turnRow}
      footer={footer}
    >
      <span className="text-sm leading-5" dangerouslySetInnerHTML={{ __html: html }} />
    </AmendWrapper>
  );

  if (turnRow) {
    return (
      <div className="flex flex-col items-start gap-2 self-stretch">
        <div className={cn(bubbleBrainClasses, 'self-stretch')}>{body}</div>
        {!optionChosen && response.options && (
          <div className="flex items-end gap-1 self-stretch px-2">
            {/* The elbow stops short of the bottom, so it meets the first option. */}
            <div className="flex items-end self-stretch pb-4">
              <div className="w-3 self-stretch rounded-bl border-b border-l border-muted-foreground opacity-50" />
            </div>
            <div className="flex flex-1 flex-wrap items-start gap-2 pt-2">
              {response.options.map((option) => (
                <button
                  key={JSON.stringify(option)}
                  className={cn(
                    'flex min-w-16 items-center justify-center gap-0 rounded-md border border-border bg-background px-2 py-1.5 text-sm leading-6 font-medium text-foreground transition',
                    isReplay ? 'pointer-events-none' : 'hover:bg-selected-soft'
                  )}
                  onClick={() => handleOptionClick(option.text)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={cn(responseClasses, amendPayload && 'w-full')}>
        {flat && icon && <div className="flex items-start gap-2 self-stretch py-1">{icon}</div>}
        {!flat && icon}

        <div className={cn(bubbleBrainClasses, flat && 'self-stretch')}>{body}</div>

        {goToButton}
      </div>

      {!optionChosen && response.options && (
        <div className="my-2 mr-9 flex flex-wrap justify-end gap-2 text-right">
          {response.options.map((option) => (
            <button
              key={JSON.stringify(option)}
              className={cn('text-sm leading-5', optionClasses, {
                'pointer-events-none': isReplay,
                'hover:bg-primary hover:text-primary-foreground': !isReplay,
              })}
              onClick={() => handleOptionClick(option.text)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};
